/**
 * RadarFilterDefaultEditor — compact editor for the school-wide default radar
 * filter (superadmin). Only the meaningful default dimensions are editable:
 * which status buckets are visible by default + the activeOnly meta toggle.
 * Structural whitelists default to "all" (empty), zones/types to "all".
 */
import type { RadarFilterState, RadarStatusBucket } from '@/services/api/teacher'
import { STATUS_BUCKET_META, DEFAULT_RADAR_FILTER, cloneFilterState } from '@/components/radar/types/filter'

interface Props {
  value: unknown
  onChange: (next: RadarFilterState) => void
}

export function RadarFilterDefaultEditor({ value, onChange }: Props) {
  const state: RadarFilterState = normalize(value)

  const toggleStatus = (bucket: RadarStatusBucket) => {
    const next = cloneFilterState(state)
    const set = new Set(next.status)
    if (set.has(bucket)) set.delete(bucket)
    else set.add(bucket)
    next.status = Array.from(set)
    onChange(next)
  }

  const toggleActiveOnly = () => {
    const next = cloneFilterState(state)
    next.activeOnly = !next.activeOnly
    onChange(next)
  }

  return (
    <div className="w-full max-w-xl border border-gray-200 rounded-xl p-3 bg-gray-50/50">
      <div className="text-[11px] text-gray-500 mb-2">
        Dieser Filter gilt schulweit als Standard und ist das Zurücksetzen-Ziel im Radar.
      </div>

      <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
        Standardmässig sichtbare Status
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {STATUS_BUCKET_META.map((m) => {
          const on = state.status.includes(m.bucket)
          return (
            <label
              key={m.bucket}
              className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer bg-white border border-gray-200 rounded-lg px-2 py-1"
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggleStatus(m.bucket)}
                className="accent-blue-500"
              />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.color }} />
              {m.labelDe}
            </label>
          )
        })}
      </div>

      <label className="flex items-center gap-2 text-xs font-medium text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          checked={state.activeOnly}
          onChange={toggleActiveOnly}
          className="accent-blue-500"
        />
        Standardmässig „Nur Aktive" (Offen + Interessiert + Übernommen)
      </label>

      <button
        type="button"
        onClick={() => onChange(cloneFilterState(DEFAULT_RADAR_FILTER))}
        className="mt-3 text-[11px] text-blue-600 hover:underline"
      >
        Auf „alles sichtbar" zurücksetzen
      </button>
    </div>
  )
}

function normalize(value: unknown): RadarFilterState {
  const base = cloneFilterState(DEFAULT_RADAR_FILTER)
  if (!value || typeof value !== 'object') return base
  const v = value as Partial<RadarFilterState>
  if (Array.isArray(v.status)) base.status = v.status as RadarStatusBucket[]
  if (Array.isArray(v.zones)) base.zones = v.zones as RadarFilterState['zones']
  if (Array.isArray(v.types)) base.types = v.types as RadarFilterState['types']
  if (v.specials && typeof v.specials === 'object') base.specials = { ...base.specials, ...v.specials }
  if (typeof v.activeOnly === 'boolean') base.activeOnly = v.activeOnly
  return base
}
